export const WAITING_ROOM_VERSION = 1;

export const WAITING_ROOM_EVENTS = {
    request: 'waiting-room/request',
    received: 'waiting-room/received',
    admit: 'waiting-room/admit',
    deny: 'waiting-room/deny',
    resolved: 'waiting-room/resolved',
};

const CONNECTED_CATEGORIES = new Set(['PNConnectedCategory', 'PNReconnectedCategory']);

export const createRequestId = (participantId) => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return `${participantId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createWaitingRoomRequest = ({ participantId, displayName, clientType }) => {
    const requestedAt = Date.now();
    return {
        type: WAITING_ROOM_EVENTS.request,
        version: WAITING_ROOM_VERSION,
        requestId: createRequestId(participantId),
        participantId,
        displayName,
        clientType,
        requestedAt,
        sentAt: requestedAt,
    };
};

export const isWaitingRoomEvent = (message, type) =>
    message &&
    message.version === WAITING_ROOM_VERSION &&
    message.type === type &&
    typeof message.requestId === 'string' &&
    typeof message.participantId === 'string';

const publishEvent = (client, room, message) =>
    client.publish({
        channel: room,
        message,
        storeInHistory: false,
    });

export const createGuestWaitingRoomController = ({
    client,
    room,
    participantId,
    clientType,
    onModeratorPresence,
    onAdmit,
    onDeny,
    retryMs = 5000,
}) => {
    let currentRequest = null;
    let acknowledged = false;
    let retryTimer = null;
    let resolving = false;

    const clearRetry = () => {
        if (retryTimer) {
            clearInterval(retryTimer);
            retryTimer = null;
        }
    };

    const refreshModeratorPresence = async () => {
        try {
            const response = await client.hereNow({
                channels: [room],
                includeState: true,
            });
            const occupants = response.channels?.[room]?.occupants || [];
            onModeratorPresence(
                occupants.some((occupant) => occupant.state?.role === 'moderator'),
            );
        } catch (error) {
            console.error('Unable to refresh moderator presence', error);
        }
    };

    const setPresence = (state) =>
        client.setState({
            channels: [room],
            state,
        });

    const sendRequest = async () => {
        if (!currentRequest || resolving) return;

        const request = { ...currentRequest, sentAt: Date.now() };
        currentRequest = request;

        const legacyName = request.displayName.trim().split(/\s+/)[0].slice(0, 20);
        await Promise.allSettled([
            setPresence({
                role: 'waiting',
                requestId: request.requestId,
                displayName: request.displayName,
                clientType: request.clientType,
                requestedAt: request.requestedAt,
            }),
            publishEvent(client, room, request),
            client.signal({
                channel: room,
                message: `request_room-${participantId}_${legacyName}`,
            }),
        ]);
    };

    const startRetry = () => {
        clearRetry();
        retryTimer = setInterval(() => {
            if (!acknowledged) sendRequest();
        }, retryMs);
    };

    const resolveRequest = async (resolution) => {
        if (!currentRequest || resolving) return;

        resolving = true;
        const request = currentRequest;
        currentRequest = null;
        acknowledged = false;
        clearRetry();

        const role = resolution === 'admit' ? 'participant' : 'idle';
        await Promise.allSettled([
            setPresence({ role }),
            publishEvent(client, room, {
                type: WAITING_ROOM_EVENTS.resolved,
                version: WAITING_ROOM_VERSION,
                requestId: request.requestId,
                participantId,
                resolution,
                sentAt: Date.now(),
            }),
        ]);

        if (resolution === 'admit') {
            await onAdmit();
        } else {
            onDeny();
        }
    };

    const messageMatchesCurrentRequest = (message, type) =>
        currentRequest &&
        isWaitingRoomEvent(message, type) &&
        message.requestId === currentRequest.requestId &&
        message.participantId === participantId;

    const handleResolution = (resolution) =>
        resolveRequest(resolution).catch((error) =>
            console.error(`Unable to process waiting-room ${resolution}`, error),
        );

    const listener = {
        message: ({ channel, message }) => {
            if (channel && channel !== room) return;

            if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.received)) {
                acknowledged = true;
                clearRetry();
            } else if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.admit)) {
                return handleResolution('admit');
            } else if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.deny)) {
                return handleResolution('deny');
            }
        },
        signal: ({ channel, message }) => {
            if (channel && channel !== room) return;

            if (message === `join_room-${participantId}`) {
                return handleResolution('admit');
            } else if (message === `deny_room-${participantId}`) {
                return handleResolution('deny');
            }
        },
        presence: (event) => {
            if (event.channel && event.channel !== room) return;

            if (
                (event.action === 'join' || event.action === 'state-change') &&
                event.state?.role === 'moderator'
            ) {
                onModeratorPresence(true);
            } else if (event.action === 'leave' || event.action === 'timeout') {
                refreshModeratorPresence();
            }
        },
        status: ({ category }) => {
            if (!CONNECTED_CATEGORIES.has(category)) return;

            refreshModeratorPresence();
            if (currentRequest) {
                acknowledged = false;
                sendRequest();
                startRetry();
            }
        },
    };

    return {
        connect() {
            client.addListener(listener);
            client.subscribe({ channels: [room], withPresence: true });
            refreshModeratorPresence();
        },
        disconnect() {
            clearRetry();
            client.removeListener(listener);
        },
        async request(displayName) {
            currentRequest = createWaitingRoomRequest({
                participantId,
                displayName,
                clientType,
            });
            acknowledged = false;
            resolving = false;
            await sendRequest();
            startRetry();
            return currentRequest;
        },
        cancel() {
            currentRequest = null;
            acknowledged = false;
            resolving = false;
            clearRetry();
        },
    };
};
